import React, { useState, useRef, useEffect } from 'react';
import { FiVolume2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { generateTTS as generateTTSCloudFunction } from '../chat/openAIUtilsCloudFunctions';
import './card.css';

function containsArabic(text) {
  if (!text) return false;
  const arabicRange = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  return arabicRange.test(text);
}

const Flashcard = ({ flashcard, onNext, onPrevious, showNavigation = false }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  // Audio state
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const audioRef = useRef(null);
  const isRTL = containsArabic(flashcard.term) || containsArabic(flashcard.translation || flashcard.explanation);

  // Reset audio state when flashcard changes
  useEffect(() => {
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    // Reset audio state
    setIsPlaying(false);
    setAudioUrl(null);
  }, [flashcard.term, flashcard.explanation, flashcard.translation]); // Reset when flashcard content changes

  const handleFlip = () => {
    if (isFlipped && onNext) {
      // If card is flipped and we click again, move to next card
      setIsFlipped(false);
      onNext();
    } else {
      // Otherwise just flip the card
      setIsFlipped(!isFlipped);
    }
  };

  const generateTTS = async (text, retryCount = 0) => {
    try {
      // Validate text before sending to API
      if (!text || text.trim().length === 0) {
        console.warn('TTS: Empty text provided');
        return null;
      }

      // Limit text length to prevent API errors
      const maxLength = 1000;
      const truncatedText = text.length > maxLength ? text.substring(0, maxLength) + '...' : text;

      console.log('TTS: Generating audio for text:', truncatedText.substring(0, 100) + '...');
      
      // Try different voices based on retry count
      const voices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
      const voiceIndex = retryCount % voices.length;
      const selectedVoice = voices[voiceIndex];
      
      console.log(`TTS: Attempt ${retryCount + 1}, using voice: ${selectedVoice}`);
      
      // Use Cloud Functions instead of direct API call
      const blob = await generateTTSCloudFunction(
        truncatedText,
        'Flashcard Generator',
        null,
        {
          model: 'tts-1',
          voice: selectedVoice,
        }
      );
      
      if (!blob) {
        throw new Error('TTS: No audio data received');
      }
      
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      console.log('TTS: Audio generated successfully');
      return url;
    } catch (e) {
      console.error('TTS Error:', e);
      
      // Retry with different voice if we haven't tried all voices yet
      const voices = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];
      if (retryCount < voices.length - 1 && (e.status === 500 || e.status === 429)) {
        console.log(`TTS: Retrying with different voice (attempt ${retryCount + 2})`);
        return generateTTS(text, retryCount + 1);
      }
      
      // Show user-friendly error message
      if (e.message && e.message.includes('TTS:')) {
        showTTSNotification(e.message, 'error');
      } else if (e.status === 401) {
        showTTSNotification('TTS: Authentication failed. Please check API key.', 'error');
      } else if (e.status === 429) {
        showTTSNotification('TTS: Rate limit exceeded. Please try again later.', 'error');
      } else if (e.status === 500) {
        showTTSNotification('TTS: Server error. Please try again later.', 'error');
      } else {
        showTTSNotification('Audio generation failed. Please try again.', 'error');
      }
      
      return null;
    }
  };

  // TTS notification system
  const [ttsNotification, setTtsNotification] = useState(null);

  const showTTSNotification = (message, type = 'info') => {
    setTtsNotification({ message, type });
    setTimeout(() => setTtsNotification(null), 4000);
  };

  const handleAudio = async () => {
    if (isPlaying) {
      // Stop current audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      return;
    }

    // Don't start if we're already generating audio
    if (isGeneratingAudio) return;

    // Determine what text to speak based on card side
    let textToSpeak = '';
    
    if (!isFlipped) {
      // Front of card - speak the term and translation
      textToSpeak = flashcard.term;
      
      // Add translation if available (not transliteration)
      if (flashcard.translation) {
        textToSpeak += `. ${flashcard.translation}`;
      }
    } else {
      // Back of card - speak the explanation and example
      textToSpeak = flashcard.explanation || flashcard.translation || '';
      
      // Add example if available
      if (flashcard.example) {
        textToSpeak += `. Example: ${flashcard.example}`;
      }
    }

    if (!textToSpeak) {
      showTTSNotification('No text available for audio', 'error');
      return;
    }

    console.log('TTS: Speaking text:', textToSpeak.substring(0, 100) + '...');

    setIsGeneratingAudio(true);
    setIsPlaying(true);
    
    try {
      // Always generate new TTS for current flashcard content
      const url = await generateTTS(textToSpeak);

      if (url && audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => {
          setIsPlaying(false);
        };
        audioRef.current.onerror = () => {
          setIsPlaying(false);
          showTTSNotification('Audio playback failed', 'error');
        };
        audioRef.current.play().catch((error) => {
          console.error('Audio play error:', error);
          setIsPlaying(false);
          showTTSNotification('Audio playback failed', 'error');
        });
      } else {
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('Audio generation error:', error);
      setIsPlaying(false);
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  // Cleanup audio URL on unmount or when audioUrl changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  return (
    <div className="flashcard-container" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* TTS Notification */}
      {ttsNotification && (
        <div 
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '8px 16px',
            borderRadius: '6px',
            color: 'white',
            fontWeight: '500',
            zIndex: 1000,
            background: ttsNotification.type === 'error' ? '#ef4444' : '#3b82f6',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            fontSize: '14px',
            maxWidth: '300px',
            textAlign: 'center'
          }}
        >
          {ttsNotification.message}
        </div>
      )}

      {/* Hidden audio element */}
      <audio ref={audioRef} style={{ display: 'none' }} />
      
      {/* Category Badge */}
      <div className="category-badge" role="note" aria-label={`Category: ${flashcard.category || 'Islamic Studies'}`}>
        Category: {flashcard.category || 'Islamic Studies'}
      </div>

      {/* Navigation Arrows */}
      <div className="flashcard-navigation">
        
        <button 
          className="nav-arrow nav-arrow-right" 
          onClick={onNext}
          aria-label="Next card"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onNext();
            }
          }}
        >
          <FiChevronRight size={24} />
        </button>

        <button 
          className="nav-arrow nav-arrow-left" 
          onClick={onPrevious}
          aria-label="Previous card"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onPrevious();
            }
          }}
        >
          <FiChevronLeft size={24} />
        </button>
      </div>

      {/* Main Flashcard */}
      <div 
        className="flashcard" 
        onClick={handleFlip}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleFlip();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={isFlipped ? "Flashcard back side - click to flip to front" : "Flashcard front side - click to flip to back"}
        aria-pressed={isFlipped}
      >
        {!isFlipped ? (
          // Front of Card
          <>
            <div className="flashcard-term" role="heading" aria-level="2">{flashcard.term}</div>
            {(flashcard.translation || flashcard.explanation) && (
              <div className="flashcard-transliteration" role="text">{flashcard.translation || flashcard.explanation}</div>
            )}
            {flashcard.transliteration && containsArabic(flashcard.term) && (
              <div className="flashcard-transliteration" role="text">{flashcard.transliteration}</div>
            )}
            <div 
              className={`flashcard-audio ${isPlaying ? 'playing' : ''} ${isGeneratingAudio ? 'generating' : ''}`} 
              onClick={(e) => { e.stopPropagation(); handleAudio(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAudio();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={
                isGeneratingAudio ? "Generating audio..." : 
                isPlaying ? "Stop audio" : 
                `Play audio: ${flashcard.term}${flashcard.translation ? ` and ${flashcard.translation}` : ''}`
              }
              aria-pressed={isPlaying}
              style={{
                opacity: ttsNotification?.type === 'error' ? 0.5 : 1,
                cursor: ttsNotification?.type === 'error' || isGeneratingAudio ? 'not-allowed' : 'pointer'
              }}
            >
              {isGeneratingAudio ? (
                <div className="audio-loading-spinner" />
              ) : (
                <FiVolume2 size={24} />
              )}
            </div>
            <div className="audio-indicator" style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))', marginTop: '4px' }}>
              {flashcard.translation ? 'Term + Translation' : 'Term only'}
            </div>
            <div className="flashcard-instruction" role="note">Click to reveal answer</div>
          </>
        ) : (
          // Back of Card
          <>
            <div className="flashcard-term" role="heading" aria-level="2">Explanation</div>
            <div className="flashcard-transliteration" role="text">{flashcard.explanation || flashcard.translation}</div>
            {flashcard.example && (
              <div className="flashcard-transliteration" style={{ fontStyle: 'italic', marginTop: '16px' }} role="text">
                Example: {flashcard.example}
              </div>
            )}
            <div 
              className={`flashcard-audio ${isPlaying ? 'playing' : ''}`} 
              onClick={(e) => { e.stopPropagation(); handleAudio(); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleAudio();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={
                isPlaying ? "Stop audio" : 
                `Play audio: ${flashcard.explanation || flashcard.translation || 'explanation'}${flashcard.example ? ' and example' : ''}`
              }
              aria-pressed={isPlaying}
            >
              <FiVolume2 size={24} />
            </div>
            <div className="audio-indicator" style={{ fontSize: '12px', color: 'hsl(var(--muted-foreground))', marginTop: '4px' }}>
              {flashcard.example ? 'Explanation + Example' : 'Explanation only'}
            </div>
            <div className="flashcard-instruction" role="note">Click to flip back</div>
          </>
        )}
      </div>

      {/* Navigation Controls */}
      {showNavigation && (
        <div className="navigation-controls" role="navigation" aria-label="Flashcard navigation">
          <button 
            className="nav-button" 
            onClick={onPrevious}
            aria-label="Go to previous card"
          >
            Previous
          </button>
          <button 
            className="nav-button primary" 
            onClick={onNext}
            aria-label="Go to next card"
          >
            Next
          </button>
        </div>
      )}

      {/* Flip Button */}
      <button 
        className="flip-button" 
        onClick={handleFlip}
        aria-label={isFlipped ? "Flip card to front" : "Flip card to back"}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
          <path d="M21 3v5h-5"/>
          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
          <path d="M3 21v-5h5"/>
        </svg>
      </button>
    </div>
  );
};

export default Flashcard; 